/**
 * Selection wizard question definitions.
 * Hardcoded defaults; steps are configurable via admin (clientAIConfig.wizards).
 */
import { getSnapshot, isStateTreeNode } from 'mobx-state-tree';
/**
 * Reads `imageAssetKey` (preferred) or legacy `imageAssetId` from stored wizard JSON.
 */
export function imageAssetKeyFromConfig(node) {
    if (!node)
        return undefined;
    const k = typeof node.imageAssetKey === 'string' ? node.imageAssetKey.trim() : '';
    if (k)
        return k;
    const legacy = typeof node.imageAssetId === 'string' ? node.imageAssetId.trim() : '';
    return legacy || undefined;
}
function readImageAssetKeyRecord(record) {
    return imageAssetKeyFromConfig(record);
}
export function isShoeSizeStep(step) {
    return 'type' in step && step.type === 'gender_size_width';
}
/** Default wizard steps (e.g. for shoes questionnaire). */
export const DEFAULT_WIZARD_STEPS = [
    {
        id: 'primary_need',
        label: 'What is your primary need for a shoe?',
        selectType: 'single',
        options: [
            { value: 'EVERYDAY_WEAR', label: 'Everyday wear' },
            { value: 'RUNNING', label: 'Running' },
            { value: 'WALKING', label: 'Walking' },
            { value: 'GYM_FITNESS', label: 'Gym / Fitness' },
            { value: 'ON_THE_JOB', label: 'On the job' },
        ],
    },
    {
        id: 'running_use',
        label: 'How will you use your running footwear?',
        selectType: 'multiple',
        options: [
            { value: 'INDOORS', label: 'Indoors' },
            { value: 'ON_THE_ROAD', label: 'On the road' },
            { value: 'RECOVERY', label: 'Recovery' },
            { value: 'ON_THE_TRAIL', label: 'On the trail' },
            { value: 'RACING', label: 'Racing' },
            { value: 'SELECT_ALL', label: 'Select all' },
        ],
    },
    {
        id: 'wear_pattern',
        label: 'Where on the bottom of your shoes do they show the most wear?',
        selectType: 'single',
        options: [
            { value: 'INSIDE', label: 'Inside' },
            { value: 'MIDDLE', label: 'Middle' },
            { value: 'OUTSIDE', label: 'Outside' },
            { value: 'NOT_SURE', label: 'Not sure' },
        ],
    },
    {
        id: 'arch',
        label: 'When looking at your footprint, what do you see?',
        selectType: 'single',
        options: [
            {
                value: 'HIGH_ARCH',
                label: 'High arch',
                description: 'Footprint shows a thin strip along the outside of the foot.',
            },
            {
                value: 'MEDIUM_ARCH',
                label: 'Medium arch',
                description: 'Footprint has a noticeable curve along the inside and shows about half of the arch.',
            },
            {
                value: 'LOW_ARCH',
                label: 'Low arch',
                description: 'Footprint shows the entire sole of your foot with little to no curve on the inside.',
            },
        ],
    },
    {
        id: 'cushion',
        label: 'How much cushion do you prefer in a shoe?',
        selectType: 'single',
        options: [
            {
                value: 'MAXIMUM',
                label: 'Maximum',
                description: 'Max cushion shoes take it further, featuring extremely thick midsoles for an exceptionally plush and soft ride, often favored by long-distance runners.',
            },
            {
                value: 'HIGH',
                label: 'High',
                description: 'Delivers a plush and cushioned experience, offering superior comfort and impact absorption.',
            },
            {
                value: 'MEDIUM',
                label: 'Medium',
                description: 'Balances comfort with versatile and moderate cushioning for various activities.',
            },
            {
                value: 'MINIMAL',
                label: 'Minimal',
                description: 'Provides moderate cushioning with a lightweight and responsive design.',
            },
        ],
    },
    {
        id: 'aches_pain',
        label: 'Do you experience any of these aches or pain when you are active?',
        selectType: 'multiple',
        options: [
            { value: 'BACK_PAIN', label: 'Back pain' },
            { value: 'HIP_PAIN', label: 'Hip pain' },
            { value: 'KNEE_PAIN', label: 'Knee pain' },
            { value: 'ANKLE_PAIN', label: 'Ankle pain' },
            { value: 'ARCH_PAIN', label: 'Arch pain' },
            { value: 'BLISTERS', label: 'Blisters' },
            { value: 'PLANTAR_FASCIITIS', label: 'Plantar fasciitis' },
            { value: 'NONE', label: 'None' },
        ],
    },
    {
        id: 'shoe_size',
        label: 'Shoe size, width and gender',
        type: 'gender_size_width',
        genderOptions: [
            { value: 'men', label: 'Men' },
            { value: 'women', label: 'Women' },
        ],
        sizeOptions: {
            men: ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '14', '15', '16'],
            women: ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '14', '15', '16'],
        },
        widthOptions: {
            men: [
                { value: 'B', label: 'B (Narrow)' },
                { value: 'D', label: 'D (Medium) – If you\'re not sure select Medium' },
                { value: '2E', label: '2E (Wide)' },
                { value: '4E', label: '4E (Extra Wide)' },
            ],
            women: [
                { value: 'B', label: 'B (Narrow)' },
                { value: 'D', label: 'D (Medium) – If you\'re not sure select Medium' },
                { value: '2E', label: '2E (Wide)' },
                { value: '4E', label: '4E (Extra Wide)' },
            ],
        },
    },
];
// Editor-only display fallbacks must not become authored JSON when a different field is edited.
const withDisplayDefaults = (raw, display) => {
    const value = { ...raw };
    Object.entries(display).forEach(([key, fallback]) => {
        if (!Object.prototype.hasOwnProperty.call(value, key)) {
            Object.defineProperty(value, key, { value: fallback, enumerable: false, configurable: true, writable: true });
        }
    });
    return value;
};
/** Normalize raw steps from config into WizardStep[]. Uses defaultSteps when raw is empty. */
export function normalizeSteps(raw, defaultSteps = DEFAULT_WIZARD_STEPS, preserveUnknown = false) {
    const normalizeLabel = (label, fallback) => {
        if (typeof label === 'string')
            return label;
        if (label && typeof label === 'object' && label.type === 'FormattedText' && typeof label.html === 'string') {
            return label;
        }
        return fallback;
    };
    if (!Array.isArray(raw) || raw.length === 0)
        return preserveUnknown && Array.isArray(raw) ? [] : [...defaultSteps];
    return raw.map((item) => {
        if (preserveUnknown && (!item || typeof item !== 'object' || Array.isArray(item)))
            return item;
        if (item && typeof item === 'object' && 'type' in item && item.type === 'gender_size_width') {
            const s = item;
            const defaultWidth = [{ value: 'D', label: 'D (Medium)' }];
            const defaultSizes = ['7', '8', '9', '10', '11', '12'];
            let genderOptions = Array.isArray(s.genderOptions)
                ? [...s.genderOptions].filter((o) => o && (preserveUnknown || (typeof o.value === 'string' && typeof o.label === 'string')))
                : [{ value: 'men', label: 'Men' }, { value: 'women', label: 'Women' }];
            if (genderOptions.length === 0) {
                genderOptions = [{ value: 'men', label: 'Men' }, { value: 'women', label: 'Women' }];
            }
            const rawSize = s.sizeOptions && typeof s.sizeOptions === 'object' && !Array.isArray(s.sizeOptions)
                ? s.sizeOptions
                : {};
            const rawWidth = s.widthOptions && typeof s.widthOptions === 'object' && !Array.isArray(s.widthOptions)
                ? s.widthOptions
                : {};
            const sizeOptions = {};
            const widthOptions = {};
            for (const g of genderOptions) {
                const key = g.value;
                const sz = rawSize[key];
                sizeOptions[key] = Array.isArray(sz) ? [...sz] : defaultSizes;
                const wd = rawWidth[key];
                widthOptions[key] = Array.isArray(wd)
                    ? wd.map((o) => ({ ...o }))
                    : defaultWidth.map((x) => ({ ...x }));
            }
            const shoe = {
                id: typeof s.id === 'string' ? s.id : 'shoe_size',
                label: normalizeLabel(s.label, 'Shoe size'),
                type: 'gender_size_width',
                genderOptions,
                sizeOptions,
                widthOptions,
            };
            const shoeKey = readImageAssetKeyRecord(s);
            if (shoeKey)
                shoe.imageAssetKey = shoeKey;
            if (typeof s.imageUrl === 'string' && s.imageUrl.trim())
                shoe.imageUrl = s.imageUrl.trim();
            return preserveUnknown ? withDisplayDefaults({
                ...s,
                ...(s.sizeOptions ? { sizeOptions: withDisplayDefaults(rawSize, sizeOptions) } : {}),
                ...(s.widthOptions ? { widthOptions: withDisplayDefaults(rawWidth, widthOptions) } : {}),
            }, shoe) : shoe;
        }
        const s = item;
        const optionsRaw = Array.isArray(s.options) ? s.options : [];
        const options = preserveUnknown ? optionsRaw.slice() : optionsRaw
            .filter((o) => Boolean(o && typeof o === 'object' && typeof o.value === 'string' && typeof o.label === 'string'))
            .map((ob) => {
            const opt = {
                value: String(ob.value),
                label: String(ob.label),
            };
            if (typeof ob.description === 'string' && ob.description.trim())
                opt.description = ob.description.trim();
            const optKey = readImageAssetKeyRecord(ob);
            if (optKey)
                opt.imageAssetKey = optKey;
            if (typeof ob.imageUrl === 'string' && ob.imageUrl.trim())
                opt.imageUrl = ob.imageUrl.trim();
            return opt;
        });
        const q = {
            id: typeof s.id === 'string' ? s.id : `q_${Date.now()}`,
            label: normalizeLabel(s.label, 'Question'),
            selectType: s.selectType === 'multiple' ? 'multiple' : 'single',
            options,
        };
        const qKey = readImageAssetKeyRecord(s);
        if (qKey)
            q.imageAssetKey = qKey;
        if (typeof s.imageUrl === 'string' && s.imageUrl.trim())
            q.imageUrl = s.imageUrl.trim();
        return preserveUnknown ? withDisplayDefaults({ ...s, ...(Array.isArray(s.options) ? { options } : {}) }, q) : q;
    });
}
/** Get normalized wizard configs from portal clientAIConfig.wizards. */
export function getWizardsFromConfig(config, preserveUnknown = false) {
    if (!config)
        return [];
    const wizardsRaw = config.wizards;
    if (!Array.isArray(wizardsRaw) || wizardsRaw.length === 0)
        return [];
    return wizardsRaw.map((w) => {
        const plain = isStateTreeNode(w) ? getSnapshot(w) : w;
        if (preserveUnknown && (!plain || typeof plain !== 'object' || Array.isArray(plain)))
            return plain;
        const o = (plain && typeof plain === 'object' && plain !== null ? plain : {});
        const id = typeof o.id === 'string' ? o.id : 'default';
        const name = typeof o.name === 'string' ? o.name : undefined;
        const stepsRaw = o.steps;
        const stepsPlain = isStateTreeNode(stepsRaw) ? getSnapshot(stepsRaw) : stepsRaw;
        const steps = normalizeSteps(stepsPlain, DEFAULT_WIZARD_STEPS, preserveUnknown);
        return preserveUnknown ? withDisplayDefaults({ ...o, ...(Array.isArray(stepsPlain) ? { steps } : {}) }, { id, name, steps }) : { id, name, steps };
    });
}
/** Bullet separator used for persistent/suggested prompt chip lists. */
export const PROMPT_LIST_SEPARATOR = '&#8226;';
export function parsePromptList(value) {
    if (!value?.trim())
        return [];
    const delimiter = value.includes(PROMPT_LIST_SEPARATOR) ? PROMPT_LIST_SEPARATOR : ',';
    return value
        .split(delimiter)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
}
export function joinPromptList(prompts) {
    return prompts.join(PROMPT_LIST_SEPARATOR);
}
function normalizePromptLabel(value) {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}
/**
 * Append configured wizard display names to a persistent-prompt chip list when missing.
 * Ensures e.g. "Fit Finder" appears as a chip whenever a fit_finder wizard is configured.
 */
export function mergeWizardLabelsIntoPromptList(promptList, config) {
    if (config?.selectionWizardEnabled === false)
        return promptList;
    const wizards = getWizardsFromConfig(config);
    if (wizards.length === 0)
        return promptList;
    const merged = parsePromptList(promptList);
    const seen = new Set(merged.map(normalizePromptLabel));
    for (const wizard of wizards) {
        const label = wizard.name?.trim() || wizard.id.replace(/_/g, ' ').trim();
        if (!label)
            continue;
        const normalized = normalizePromptLabel(label);
        if (seen.has(normalized))
            continue;
        merged.push(label);
        seen.add(normalized);
    }
    return joinPromptList(merged);
}
//# sourceMappingURL=wizardData.js.map